import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, Clock, DollarSign, Sparkles, Trash2, Plus, TrendingUp, Lock, Bookmark, BookmarkCheck, Zap, Mail, Settings, Share2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import GigsTab from "@/pages/GigsTab";
import ApplicationsTab from "@/pages/ApplicationsTab";
import SettingsModal from "@/components/SettingsModal";

function SaveBtn({ hustleId, saved, onToggle, stop }) {
  const { user } = useAuth();
  const click = async (e) => {
    if (stop) { e.preventDefault(); e.stopPropagation(); }
    if (!user) return toast.error("Sign in to save hustles");
    try {
      const { data } = await api.post(`/hustles/${hustleId}/save`);
      onToggle?.(data);
      toast.success(data.saved ? "Saved to shortlist" : "Removed from shortlist");
    } catch { toast.error("Failed"); }
  };
  return (
    <button onClick={click} className={`p-2 rounded-full transition ${saved ? "text-[var(--copper)]" : "text-[var(--stone)] hover:text-[var(--forest)]"}`}
      aria-label={saved ? "Remove from shortlist" : "Save to shortlist"}
      data-testid={`save-btn-${hustleId}`}>
      {saved ? <BookmarkCheck size={18} strokeWidth={2.5}/> : <Bookmark size={18}/>}
    </button>
  );
}

function HustleCard({ h, showMatch, savedIds, onSaveToggle }) {
  const isSaved = savedIds?.includes(h.id);
  return (
    <Link to={`/hustle/${h.id}`} className="card-warm p-6 block relative" data-testid={`hustle-card-${h.id}`}>
      <div className="absolute top-3 right-3">
        <SaveBtn hustleId={h.id} saved={isSaved} onToggle={onSaveToggle} stop/>
      </div>
      <div className="flex justify-between items-start mb-3 pr-10">
        <span className="tag-pill">{h.category}</span>
        {showMatch && <span className="match-badge">{h.match}% match</span>}
      </div>
      <h3 className="font-display text-xl font-bold leading-tight">{h.name}</h3>
      <p className="text-sm text-[var(--stone)] mt-1 italic">"{h.tagline}"</p>
      <div className="flex flex-wrap gap-3 mt-4 text-sm">
        <div className="flex items-center gap-1"><DollarSign size={14} className="text-[var(--copper)]"/>
          <span className="font-mono">${h.monthly_low.toLocaleString()}–${h.monthly_high.toLocaleString()}/mo</span></div>
        <div className="flex items-center gap-1 text-[var(--stone)]"><Clock size={14}/>
          <span className="font-mono">{h.hours_low}–{h.hours_high}h/wk</span></div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-[var(--stone)]">Startup: <b className="text-[var(--charcoal)] font-mono">${h.startup_cost}</b></span>
        <span className="tag-pill" style={{background:"transparent"}}>{h.difficulty}</span>
      </div>
    </Link>
  );
}

function MatchesTab({ savedIds, onSaveToggle }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/matches").then(r => setData(r.data)).catch(e => {
      if (e?.response?.status === 400) nav("/quiz");
    }).finally(() => setLoading(false));
  }, [nav]);
  if (loading) return <div className="py-12 text-center text-[var(--stone)]">Matching you…</div>;
  if (!data) return null;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
        <div>
          <div className="caption">Your AI matches</div>
          <h2 className="font-display text-3xl font-black">{data.matches.length} hustles ranked for you</h2>
        </div>
        {!data.pro && (
          <div className="text-sm text-[var(--stone)]">
            Showing top 5. <Link to="/pricing" className="text-[var(--forest)] font-semibold underline">Unlock all {data.total_available}</Link>
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.matches.map(h => <HustleCard key={h.id} h={h} showMatch savedIds={savedIds} onSaveToggle={onSaveToggle}/>)}
      </div>
      {!data.pro && (
        <div className="card-warm p-8 mt-8 text-center" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
          <Lock className="mx-auto mb-3" size={28}/>
          <h3 className="font-display text-2xl font-bold">Unlock the whole match list</h3>
          <p className="opacity-80 mt-2">Plus AI blueprints, income tracker, and unlimited quizzes.</p>
          <Link to="/pricing" className="btn-copper mt-5 inline-flex" data-testid="matches-upgrade-btn">Upgrade to Pro</Link>
        </div>
      )}
    </div>
  );
}

function DirectoryTab({ savedIds, onSaveToggle }) {
  const [hustles, setHustles] = useState([]);
  const [filter, setFilter] = useState({ category: "all", difficulty: "all" });
  useEffect(() => { api.get("/hustles").then(r => setHustles(r.data)); }, []);
  const filtered = useMemo(() => hustles.filter(h =>
    (filter.category === "all" || h.category === filter.category) &&
    (filter.difficulty === "all" || h.difficulty === filter.difficulty)
  ), [hustles, filter]);
  const categories = [...new Set(hustles.map(h => h.category))];
  return (
    <div>
      <div className="mb-6">
        <div className="caption">Browse everything</div>
        <h2 className="font-display text-3xl font-black">The full hustle library</h2>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={filter.category} onValueChange={(v)=>setFilter({...filter, category: v})}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category"><SelectValue placeholder="Category"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.difficulty} onValueChange={(v)=>setFilter({...filter, difficulty: v})}>
          <SelectTrigger className="w-[180px]" data-testid="filter-difficulty"><SelectValue placeholder="Difficulty"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="Easy">Easy</SelectItem>
            <SelectItem value="Intermediate">Intermediate</SelectItem>
            <SelectItem value="Advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(h => <HustleCard key={h.id} h={h} savedIds={savedIds} onSaveToggle={onSaveToggle}/>)}
      </div>
    </div>
  );
}

function SavedTab({ savedIds, onSaveToggle }) {
  const [hustles, setHustles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState(false);
  const load = () => api.get("/saved").then(r => setHustles(r.data.hustles)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [savedIds?.join(",")]);
  if (loading) return <div className="py-12 text-center text-[var(--stone)]">Loading your shortlist…</div>;
  if (hustles.length === 0) {
    return (
      <div className="card-warm p-10 text-center">
        <Bookmark size={32} className="mx-auto text-[var(--stone)] mb-3"/>
        <h3 className="font-display text-2xl font-bold">Your shortlist is empty.</h3>
        <p className="text-[var(--stone)] mt-2">Tap the bookmark on any hustle to build your comparison list.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="caption">Your shortlist</div>
          <h2 className="font-display text-3xl font-black">{hustles.length} saved hustle{hustles.length===1?"":"s"}</h2>
        </div>
        <button onClick={()=>setCompare(!compare)} className="btn-outline" data-testid="toggle-compare-btn">
          {compare ? "Card view" : "Compare side-by-side"}
        </button>
      </div>

      {compare ? (
        <div className="card-warm p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--alabaster-2)] text-left">
              <tr>
                <th className="p-4 caption">Metric</th>
                {hustles.map(h => (
                  <th key={h.id} className="p-4 min-w-[180px]">
                    <div className="font-display text-base font-bold">{h.name}</div>
                    <div className="text-xs text-[var(--stone)] italic mt-1">"{h.tagline}"</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Category</td>
                {hustles.map(h => <td key={h.id} className="p-4">{h.category}</td>)}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Monthly</td>
                {hustles.map(h => <td key={h.id} className="p-4 font-mono font-bold text-[var(--forest)]">${h.monthly_low.toLocaleString()}–${h.monthly_high.toLocaleString()}</td>)}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Hours / week</td>
                {hustles.map(h => <td key={h.id} className="p-4 font-mono">{h.hours_low}–{h.hours_high}h</td>)}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Startup cost</td>
                {hustles.map(h => <td key={h.id} className="p-4 font-mono">${h.startup_cost}</td>)}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Difficulty</td>
                {hustles.map(h => <td key={h.id} className="p-4">{h.difficulty}</td>)}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">$/hr est.</td>
                {hustles.map(h => {
                  const midMo = (h.monthly_low + h.monthly_high) / 2;
                  const midHrs = ((h.hours_low + h.hours_high) / 2) * 4.3;
                  const rate = midHrs > 0 ? Math.round(midMo / midHrs) : 0;
                  return <td key={h.id} className="p-4 font-mono text-[var(--copper)] font-bold">${rate}</td>;
                })}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4 caption">Tools</td>
                {hustles.map(h => (
                  <td key={h.id} className="p-4">
                    <div className="flex flex-wrap gap-1">{(h.tools||[]).map(t => <span key={t} className="tag-pill">{t}</span>)}</div>
                  </td>
                ))}
              </tr>
              <tr className="border-t border-[var(--border-warm)]">
                <td className="p-4"></td>
                {hustles.map(h => (
                  <td key={h.id} className="p-4">
                    <div className="flex gap-2">
                      <Link to={`/hustle/${h.id}`} className="text-xs font-semibold underline text-[var(--forest)]">Open</Link>
                      <button onClick={async ()=>{const {data}=await api.post(`/hustles/${h.id}/save`); onSaveToggle?.(data); toast.success("Removed");}}
                              className="text-xs font-semibold underline text-[var(--stone)]" data-testid={`compare-remove-${h.id}`}>Remove</button>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hustles.map(h => <HustleCard key={h.id} h={h} savedIds={savedIds} onSaveToggle={onSaveToggle}/>)}
        </div>
      )}
    </div>
  );
}

function CommunityTab() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", earnings: "" });
  const load = () => api.get("/community").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const vote = async (id) => {
    if (!user) return toast.error("Log in to upvote");
    try {
      const { data } = await api.post(`/community/${id}/upvote`);
      setPosts(posts.map(p => p.id === id ? { ...p, upvotes: data.upvotes } : p));
    } catch { toast.error("Failed"); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/community", { ...form, earnings: form.earnings ? Number(form.earnings) : null });
      setOpen(false); setForm({ title: "", body: "", earnings: "" });
      toast.success("Posted!"); load();
    } catch { toast.error("Failed to post"); }
  };
  return (
    <div>
      <div className="flex justify-between items-baseline mb-6 flex-wrap gap-2">
        <div>
          <div className="caption">Real wins</div>
          <h2 className="font-display text-3xl font-black">Community feed</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="community-post-btn"><Plus size={16}/> Share a win</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Share your side hustle win</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Title</Label><Input required value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="Hit $500 in month 1" data-testid="post-title-input"/></div>
              <div><Label>What worked?</Label><Textarea required rows={4} value={form.body} onChange={e=>setForm({...form, body:e.target.value})} placeholder="Details, tools, lessons…" data-testid="post-body-input"/></div>
              <div><Label>Earnings ($, optional)</Label><Input type="number" value={form.earnings} onChange={e=>setForm({...form, earnings:e.target.value})} placeholder="500" data-testid="post-earnings-input"/></div>
              <button type="submit" className="btn-primary w-full justify-center" data-testid="post-submit-btn">Post</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        {posts.map(p => (
          <div key={p.id} className="card-warm p-6 flex gap-4" data-testid={`community-post-${p.id}`}>
            <button onClick={()=>vote(p.id)} className="flex flex-col items-center gap-1 min-w-[44px]" data-testid={`upvote-btn-${p.id}`}>
              <ArrowUp size={20} className="text-[var(--forest)]"/>
              <span className="font-mono text-sm font-bold">{p.upvotes}</span>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2 justify-between">
                <h3 className="font-display text-xl font-bold">{p.title}</h3>
                {p.earnings && <span className="font-mono text-[var(--copper)] font-bold">+${p.earnings.toLocaleString()}</span>}
              </div>
              <p className="text-[var(--stone)] mt-1 break-words">{p.body}</p>
              <div className="text-xs text-[var(--stone)] mt-3">by {p.user_name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackerTab() {
  const { user } = useAuth();
  const [data, setData] = useState({ logs: [], total: 0 });
  const [form, setForm] = useState({ hustle_name: "", amount: "", hours: "", note: "" });
  const [locked, setLocked] = useState(false);
  const load = () => api.get("/income").then(r => setData(r.data)).catch(e => {
    if (e?.response?.status === 402) setLocked(true);
  });
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <div className="py-12 text-center text-[var(--stone)]">Log in to track income.</div>;
  if (locked) return (
    <div className="card-warm p-10 text-center" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
      <Lock className="mx-auto mb-3" size={28}/>
      <h3 className="font-display text-2xl font-bold">Income tracker is Pro-only</h3>
      <p className="opacity-80 mt-2">Log every dollar, chart your growth, set targets.</p>
      <Link to="/pricing" className="btn-copper mt-5 inline-flex" data-testid="tracker-upgrade-btn">Upgrade to Pro</Link>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/income", {
        hustle_name: form.hustle_name, amount: Number(form.amount),
        hours: form.hours ? Number(form.hours) : null, note: form.note,
      });
      setForm({ hustle_name: "", amount: "", hours: "", note: "" });
      toast.success("Logged!"); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/income/${id}`); load(); };

  // Chart: sum per day
  const byDay = {};
  data.logs.forEach(l => {
    const d = (l.date || "").slice(0, 10);
    byDay[d] = (byDay[d] || 0) + l.amount;
  });
  const chart = Object.entries(byDay).sort((a,b)=>a[0].localeCompare(b[0])).slice(-14).map(([d,v])=>({date: d.slice(5), amount: v}));

  return (
    <div>
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="card-warm p-6" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
          <div className="caption" style={{color:"#D96B27"}}>Total earned</div>
          <div className="font-display text-4xl font-black mt-2 font-mono" data-testid="tracker-total">${data.total.toLocaleString()}</div>
        </div>
        <div className="card-warm p-6 lg:col-span-2">
          <div className="caption mb-2">Last 14 days</div>
          {chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD3"/>
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11}/>
                <YAxis stroke="#6B7280" fontSize={11}/>
                <Tooltip/>
                <Line type="monotone" dataKey="amount" stroke="#D96B27" strokeWidth={2} dot={{fill:"#0F3D2E"}}/>
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-[var(--stone)] py-6 text-center">Log your first win to see the chart.</div>}
        </div>
      </div>

      <form onSubmit={submit} className="card-warm p-6 mb-6 grid md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2"><Label>Hustle</Label><Input required value={form.hustle_name} onChange={e=>setForm({...form, hustle_name:e.target.value})} placeholder="Freelance web dev" data-testid="tracker-hustle-input"/></div>
        <div><Label>Amount ($)</Label><Input required type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} placeholder="250" data-testid="tracker-amount-input"/></div>
        <div><Label>Hours</Label><Input type="number" step="0.5" value={form.hours} onChange={e=>setForm({...form, hours:e.target.value})} placeholder="4" data-testid="tracker-hours-input"/></div>
        <button type="submit" className="btn-primary justify-center" data-testid="tracker-add-btn"><Plus size={16}/> Log</button>
      </form>

      <div className="space-y-2">
        {data.logs.map(l => (
          <div key={l.id} className="card-warm p-4 flex items-center gap-4">
            <TrendingUp size={18} className="text-[var(--copper)]"/>
            <div className="flex-1">
              <div className="font-semibold">{l.hustle_name}</div>
              <div className="text-xs text-[var(--stone)]">{new Date(l.date).toLocaleDateString()} {l.hours ? `· ${l.hours}h` : ""}</div>
            </div>
            <div className="font-mono font-bold text-[var(--forest)]">${l.amount.toLocaleString()}</div>
            <button onClick={()=>del(l.id)} className="text-[var(--stone)] hover:text-red-500"><Trash2 size={16}/></button>
          </div>
        ))}
        {data.logs.length === 0 && <div className="text-center text-[var(--stone)] py-6">No entries yet. Log your first win above.</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "matches";
  const [savedIds, setSavedIds] = useState(user?.saved || []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => { if (!user) nav("/"); else setSavedIds(user.saved || []); }, [user, nav]);
  const onSaveToggle = (data) => { setSavedIds(data.ids); refresh(); };
  if (!user) return null;

  return (
    <div className="min-h-screen">
      <HeaderNav/>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 lg:py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="caption">Hey {user.name}</div>
            <h1 className="font-display text-3xl sm:text-5xl font-black mt-1">Your hustle HQ.</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {user.handle && user.is_public && (
              <a href={`/u/${user.handle}`} target="_blank" rel="noopener noreferrer"
                 className="btn-outline !py-2 text-sm" data-testid="view-public-profile-btn">
                <Share2 size={14}/> My public profile
              </a>
            )}
            <button
              onClick={async ()=>{
                try {
                  await api.post("/digest/send-me");
                  toast.success("Digest sent to " + user.email);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to send");
                }
              }}
              className="btn-outline !py-2 text-sm" data-testid="preview-digest-btn">
              <Mail size={14}/> Preview digest
            </button>
            <button onClick={()=>setSettingsOpen(true)} className="btn-primary !py-2 text-sm" data-testid="open-settings-btn">
              <Settings size={14}/> Settings
            </button>
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v)=>setParams({tab: v})}>
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="matches" data-testid="tab-matches">My matches</TabsTrigger>
            <TabsTrigger value="gigs" data-testid="tab-gigs">
              <Zap size={14} className="mr-1"/> Live gigs
            </TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved">
              Saved {savedIds.length > 0 && <span className="ml-1.5 text-xs font-mono">({savedIds.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="community" data-testid="tab-community">Community</TabsTrigger>
            <TabsTrigger value="tracker" data-testid="tab-tracker">Income tracker</TabsTrigger>
          </TabsList>
          <TabsContent value="matches"><MatchesTab savedIds={savedIds} onSaveToggle={onSaveToggle}/></TabsContent>
          <TabsContent value="gigs"><GigsTab/></TabsContent>
          <TabsContent value="applications"><ApplicationsTab/></TabsContent>
          <TabsContent value="directory"><DirectoryTab savedIds={savedIds} onSaveToggle={onSaveToggle}/></TabsContent>
          <TabsContent value="saved"><SavedTab savedIds={savedIds} onSaveToggle={onSaveToggle}/></TabsContent>
          <TabsContent value="community"><CommunityTab/></TabsContent>
          <TabsContent value="tracker"><TrackerTab/></TabsContent>
        </Tabs>
      </div>
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen}/>
    </div>
  );
}
